import React, { useState, useEffect } from 'react'
import { TableProps, RowDataType, RowKeyType } from 'rsuite-table'
import { Loader } from 'shared/atoms'
import TableFooter from '../table/footer/TableFooter'
import CropCard from './CropCard'
import CropIcon from '../../../assets/images/default-crop.svg'
import './CardTable.scss'
import FarmCard from './FarmCard'
import { ReactComponent as NoData } from '../../../assets/images/no-farms.svg'
import FarmTypeCard from './FarmTypeCard'
import WhatsAppInfoCard from './WhatsAppInfo'
import VerifyDocumentCard from './VerifyDocumentCard'

export interface Pagination {
  page: number;
  limit: number;
}

const DEFAULT_PAGE_SIZE = 10

function CardTable<R extends RowDataType<any>, K extends RowKeyType>(
  props: TableProps<R, K> & {
    paginated?: boolean,
    pageSizeOptions?: number[],
    defaultPageSize?: number,
    total?: number,
    loading: boolean,
    onPageChange?: (page: number, pageSize: number) => void,
    maxButtons?: number,
    actionOptions?: string[],
    onAction?: (eventKey: string | undefined, rowData: any) => void,
    handleSelection?: (rowData: any) => void,
    handleCardClick?: (rowData: any) => void,
    confirmDelete?: (rowData: any) => void,
    handleVerifyDoc?: (rowData: any) => void,
    onReject?: (rowData: any) => void,
    selected?: any[],
    cropCard?: boolean,
    farmCard?: boolean,
    farmCardTask?: boolean,
    documentCard?: boolean,
    farmTypeCard?: boolean,
    whatsAppInfo?: boolean
  }
): JSX.Element {
  const {
    data = [],
    paginated = false,
    pageSizeOptions,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    total = data.length,
    loading,
    onPageChange,
    maxButtons = 3,
    actionOptions,
    onAction,
    handleSelection,
    handleVerifyDoc,
    onReject,
    selected = [],
    cropCard,
    documentCard,
    farmCard,
    farmCardTask,
    farmTypeCard,
    whatsAppInfo,
    handleCardClick,
    confirmDelete
  } = props

  const initialPageSize =
    pageSizeOptions && pageSizeOptions.length > 0
      ? pageSizeOptions[0]
      : defaultPageSize

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)

  const handlePageChangeInternal = (page: number) => {
    setCurrentPage(page)
    if (onPageChange) {
      onPageChange(page, pageSize)
    }
  }

  const handlePageSizeChangeInternal = (
    value: number | null,
    _: React.SyntheticEvent
  ) => {
    if (value !== null) {
      setPageSize(value)
      setCurrentPage(1)
      if (onPageChange) {
        onPageChange(1, value)
      }
    }
  }

  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPage, pageSize)
    }
    // eslint-disable-next-line
  }, [currentPage, pageSize])
  let className = 'card-wrapper'

  if (farmCard) {
    className += ' farm-wrapper'
  } else if (farmCardTask) {
    className += ' task-wrapper'
  } else if (farmTypeCard || whatsAppInfo) {
    className += ' farmType-wrapper'
  } else if (documentCard) {
    className += ' document-wrapper'
  }

  return (
    <div className="card-container">
      {data.length === 0 && !loading && (
        <div className="no-data">
          <NoData />
          <div>No Data Found</div>
        </div>
      )}
      {loading && <Loader />}
      <div className={className}>
        {!loading &&
          data?.map((item) => (
            <div key={item?.id}>
              {cropCard && (
                <CropCard
                  imageUrl={item?.imageUrl ?? CropIcon}
                  onAction={onAction}
                  data={item}
                  actionOptions={actionOptions}
                  handleSelection={handleSelection}
                  selected={selected?.some((value) => value?.id === item?.id)}
                  handleCardClick={handleCardClick}
                />
              )}
              {farmCard && (
                <FarmCard
                  imageUrl={item?.farmImage ?? CropIcon}
                  title={item?.farmName}
                  subtitle={item?.cropName}
                  date={item?.createdDate}
                  totalIncome={item?.totalCreditAmount}
                  totalExpense={item?.totalDebitAmount}
                  farmAreaUnit={item?.farmAreaUnit?.farmAreaUnitNames[0]?.name}
                  noOfAnimals={item?.noOfAnimals}
                  farmArea={item?.farmArea}
                  onAction={onAction}
                  handleSelection={() => handleSelection?.(item)}
                  actionOptions={actionOptions}
                  data={item}
                />
              )}

              {farmTypeCard && (
                <FarmTypeCard
                  data={item}
                  key={item.id}
                  globalName={item.globalName}
                  imageUrl={item.imageUrl}
                  handleCardClick={handleSelection}
                />
              )}
              {documentCard && (
                <VerifyDocumentCard
                  imageUrl={
                    item?.imageUrls ??
                    item?.passbookImage ??
                    item?.documentUrl ??
                    CropIcon
                  }
                  title={
                    item?.documentType ?? item?.bankName ?? item?.documentName
                  }
                  number={item?.documentNo ?? item?.accountNumber}
                  code={item?.ifscCode}
                  handleSelection={() => handleSelection?.(item)}
                  handleVerifyDoc={handleVerifyDoc}
                  handleCardClick={handleCardClick}
                  onReject={onReject}
                  data={item}
                />
              )}
              {whatsAppInfo && (
                <WhatsAppInfoCard
                  data={
                    item as unknown as {
                      id?: string,
                      status?: string,
                      subsId?: number,
                      channel?: string,
                      channelId?: string,
                      phone_id?: string,
                      businessInfo?: string
                    }
                  }
                  key={item.id}
                  confirmDelete={confirmDelete}
                />
              )}
            </div>
          ))}
      </div>
      {paginated && (
        <TableFooter
          total={total}
          pageSize={pageSize}
          currentPage={currentPage}
          pageSizeOptions={pageSizeOptions}
          onPageChange={handlePageChangeInternal}
          onPageSizeChange={handlePageSizeChangeInternal}
          maxButtons={maxButtons}
        >
          {`${(currentPage - 1) * pageSize + 1}-${Math?.min(
            currentPage * pageSize,
            total
          )} of ${total}`}
        </TableFooter>
      )}
    </div>
  )
}

CardTable.DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE

export default CardTable
